// Product Comparison System - Similar to RdnlWishlist
(function() {
  'use strict';

  const STORAGE_KEY = 'rendl_compare';
  const MAX_COMPARE_ITEMS = 20;

  // Get comparison list from localStorage
  function getCompareList() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Error parsing compare list from localStorage:', e);
      return [];
    }
  }

  // Save comparison list to localStorage
  function saveCompareList(compareList) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(compareList));
      // Dispatch event for other scripts to listen
      document.dispatchEvent(new CustomEvent('compare:updated', { 
        detail: { count: compareList.length, items: compareList }
      }));
      return true;
    } catch (e) {
      console.error('Error saving compare list to localStorage:', e);
      return false;
    }
  }

  // Add product to comparison
  function addToCompare(productData) {
    const compareList = getCompareList();
    
    // Check if product already exists
    const existingIndex = compareList.findIndex(item => item.id === productData.id);
    if (existingIndex !== -1) {
      console.log('Product already in comparison list');
      return false;
    }

    // Check maximum limit (removed for unlimited comparison)
    // if (compareList.length >= MAX_COMPARE_ITEMS) {
    //   alert(`You can compare maximum ${MAX_COMPARE_ITEMS} products at once.`);
    //   return false;
    // }

    // Add product with timestamp
    const productToAdd = {
      ...productData,
      addedAt: Date.now()
    };

    compareList.push(productToAdd);
    saveCompareList(compareList);
    
    console.log('Product added to comparison:', productToAdd.title);
    return true;
  }

  // Remove product from comparison
  function removeFromCompare(productId) {
    const compareList = getCompareList();
    const filteredList = compareList.filter(item => item.id !== productId);
    
    if (filteredList.length !== compareList.length) {
      saveCompareList(filteredList);
      console.log('Product removed from comparison:', productId);
      return true;
    }
    
    return false;
  }

  // Check if product is in comparison
  function isInCompare(productId) {
    const compareList = getCompareList();
    return compareList.some(item => item.id === productId);
  }

  // Clear all products from comparison
  function clearCompare() {
    saveCompareList([]);
    console.log('All products removed from comparison');
    return true;
  }

  // Get product data from Shopify API
  async function fetchProductData(productId, productHandle = null) {
    try {
      // Try different endpoints
      let response;
      
      if (productHandle) {
        response = await fetch(`/products/${productHandle}.js`);
      } else {
        response = await fetch(`/products/${productId}.js`);
      }

      if (!response.ok) {
        throw new Error('Product not found');
      }

      const product = await response.json();
      const variant = product.variants.find(v => v.available) || product.variants[0];

      return {
        id: product.id.toString(),
        handle: product.handle,
        title: product.title,
        price: formatPrice(variant.price),
        compareAtPrice: variant.compare_at_price ? formatPrice(variant.compare_at_price) : null,
        image: variant.featured_image ? variant.featured_image.src : (product.featured_image || ''),
        url: `/products/${product.handle}`,
        available: variant.available,
        variantId: variant.id,
        vendor: product.vendor || '',
        type: product.type || ''
      };
    } catch (error) {
      console.error('Error fetching product data:', error);
      throw error;
    }
  }

  // Format price
  function formatPrice(price) {
    if (!price) return '';
    
    // Use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(price);
    }
    
    // Fallback formatting
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price / 100);
  }

  // Add product to compare with data attributes
  async function addProductToCompare(productId, productTitle = null, productHandle = null) {
    try {
      console.log('Adding product to compare:', { productId, productTitle, productHandle });
      // First check if already in compare
      if (isInCompare(productId)) {
        console.log('Product already in comparison');
        return false;
      }
      
      // Try to get data from product card first
      const productCard = document.querySelector(`[data-product-id="${productId}"]`);
      let productData;
      
      if (productCard) {
        // Debug: log all data attributes
        console.log('All data attributes for product card:', productCard.dataset);
        if (!productCard.dataset.height && !productCard.dataset.length) {
          console.warn('Both data-height and data-length are empty for this product card!');
        }
        
        // Find image element
        const imageElement = productCard.querySelector('.product-image') || 
                           productCard.querySelector('img.product-image') || 
                           productCard.querySelector('.card__media img') ||
                           productCard.querySelector('img');
        // Get data from card attributes
        console.log('Getting data from product card attributes');
        productData = {
          id: productId.toString(),
          handle: productHandle || productId,
          title: productTitle || productCard.querySelector('.card__heading a')?.textContent?.trim() || 'Product',
          description: productCard.dataset.description || '',
          price: productCard.dataset.price || 'Check product page',
          compareAtPrice: productCard.dataset.compareAtPrice || null,
          available: productCard.dataset.available === 'true',
          image: (() => {
            const img = imageElement;
            if (!img) return 'https://via.placeholder.com/200?text=No+Image';
            
            // Try to get src first
            if (img.src && img.src !== '') {
              return img.src;
            }
            
            // If no src, try to extract from srcset
            if (img.srcset && img.srcset !== '') {
              const srcsetEntries = img.srcset.split(',');
              // Get the largest image from srcset
              const largestEntry = srcsetEntries[srcsetEntries.length - 1];
              const url = largestEntry.trim().split(' ')[0];
              return url;
            }
            
            return 'https://via.placeholder.com/200?text=No+Image';
          })(),
          secondImage: (() => {
            // Find second image in the same card
            const allImages = productCard.querySelectorAll('img.product-image');
            if (allImages.length > 1) {
              const secondImg = allImages[1];
              if (secondImg.src && secondImg.src !== '') {
                return secondImg.src;
              }
              if (secondImg.srcset && secondImg.srcset !== '') {
                const srcsetEntries = secondImg.srcset.split(',');
                const largestEntry = srcsetEntries[srcsetEntries.length - 1];
                const url = largestEntry.trim().split(' ')[0];
                return url;
              }
            }
            return null;
          })(),
          url: productCard.querySelector('.card__heading a')?.href || `/products/${productHandle || productId}`,
          variantId: productCard.dataset.variantId || null,
          // Collect all metafields from data attributes
          metafields: {
            product_attributes: {
              barcode: productCard.dataset.barcode || '',
              ip_rate: productCard.dataset.ipRate || '',
              lumens: productCard.dataset.lumens || '',
              power_source: productCard.dataset.powerSource || '',
              Watt: productCard.dataset.watt || '',
              kelvin: productCard.dataset.kelvin || '',
              light_color: productCard.dataset.lightColor || '',
              Diameter: productCard.dataset.diameter || '',
              Length: productCard.getAttribute('data-Length') || productCard.dataset.length || '',
              Weight_netto: productCard.dataset.weightNetto || '',
              Weight_unit: productCard.dataset.weightUnit || '',
              mounting_hole_size: productCard.dataset.mountingHoleSize || '',
              recess_depth: productCard.dataset.recessDepth || '',
              suspension_lenght: productCard.dataset.suspensionLenght || '',
              turn_angle: productCard.dataset.turnAngle || '',
              tilt_angle: productCard.dataset.tiltAngle || '',
              switch: productCard.dataset.switch || '',
              socket: productCard.dataset.socket || '',
              light_source_recomended: productCard.dataset.lightSourceRecomended || '',
              height: productCard.dataset.height || '',
              length: productCard.dataset.length || productCard.getAttribute('data-Length') || '',
            }
          }
        };
        
        console.log('Product data from card:', productData);
      } else {
        // Fallback to API if card not found
        console.log('Product card not found, using API fallback');
        try {
          let response;
          if (productHandle) {
            response = await fetch(`/products/${productHandle}.js`);
          } else {
            response = await fetch(`/products/${productId}.js`);
          }
          if (!response.ok) throw new Error('Product not found');
          const product = await response.json();
          const variant = product.variants.find(v => v.available) || product.variants[0];
          
          productData = {
            id: product.id.toString(),
            handle: product.handle,
            title: product.title,
            description: product.description ? product.description.substring(0, 150) + (product.description.length > 150 ? '...' : '') : '',
            price: window.Shopify && window.Shopify.formatMoney ? window.Shopify.formatMoney(variant.price) : (variant.price / 100).toFixed(2) + '€',
            compareAtPrice: variant.compare_at_price ? (window.Shopify && window.Shopify.formatMoney ? window.Shopify.formatMoney(variant.compare_at_price) : (variant.compare_at_price / 100).toFixed(2) + '€') : null,
            available: variant.available,
            image: variant.featured_image ? variant.featured_image.src : (product.featured_image || ''),
            secondImage: product.images && product.images.length > 1 ? product.images[1].src : null,
            url: `/products/${product.handle}`,
            variantId: variant.id,
            metafields: {
              product_attributes: {
                barcode: variant.barcode || '',
                ip_rate: variant.metafields?.product_attributes?.ip_rate || '',
                lumens: variant.metafields?.product_attributes?.lumens || '',
                power_source: variant.metafields?.product_attributes?.power_source || '',
                Watt: variant.metafields?.product_attributes?.Watt || '',
                kelvin: variant.metafields?.product_attributes?.kelvin || '',
                light_color: variant.metafields?.product_attributes?.light_color || '',
                Diameter: variant.metafields?.product_attributes?.Diameter || '',
                Length: variant.metafields?.product_attributes?.Length || variant.metafields?.product_attributes?.length || '',
                Weight_netto: variant.metafields?.product_attributes?.Weight_netto || '',
                Weight_unit: variant.metafields?.product_attributes?.Weight_unit || '',
                mounting_hole_size: variant.metafields?.product_attributes?.mounting_hole_size || '',
                recess_depth: variant.metafields?.product_attributes?.recess_depth || '',
                suspension_lenght: variant.metafields?.product_attributes?.suspension_lenght || '',
                turn_angle: variant.metafields?.product_attributes?.turn_angle || '',
                tilt_angle: variant.metafields?.product_attributes?.tilt_angle || '',
                switch: variant.metafields?.product_attributes?.switch || '',
                socket: variant.metafields?.product_attributes?.socket || '',
                light_source_recomended: variant.metafields?.product_attributes?.light_source_recomended || '',
                height: variant.metafields?.product_attributes?.height || '',
                length: variant.metafields?.product_attributes?.length || variant.metafields?.product_attributes?.Length || '',
              }
            }
          };
        } catch (error) {
          // Final fallback with basic data
          console.log('Using final fallback data for product:', productId);
          productData = {
            id: productId.toString(),
            handle: productHandle || productId,
            title: productTitle || 'Product',
            description: '',
            price: 'Check product page',
            compareAtPrice: null,
            available: true,
            image: 'https://via.placeholder.com/200?text=No+Image',
            secondImage: null,
            url: `/products/${productHandle || productId}`,
            variantId: null,
            metafields: {
              product_attributes: {}
            },
          };
        }
      }
      
      return addToCompare(productData);
    } catch (error) {
      console.error('Error adding product to compare:', error);
      return false;
    }
  }

  // Update compare counters in UI
  function updateCompareCounters(count = null) {
    if (count === null) {
      const compareList = getCompareList();
      count = compareList.length;
    }

    // Update compare count elements
    const compareCounters = document.querySelectorAll('.compare-count, .compare-count-bubble span[aria-hidden="true"]');
    compareCounters.forEach(counter => {
      counter.textContent = count;
    });

    // Update compare badges
    const compareBadges = document.querySelectorAll('.compare-count-bubble');
    compareBadges.forEach(badge => {
      if (count > 0) {
        badge.style.display = 'flex';
        badge.classList.remove('hidden');
      } else {
        badge.style.display = 'none';
        badge.classList.add('hidden');
      }
    });

    // Update page-specific compare count (like in compare page)
    const pageCompareCount = document.getElementById('compare-count');
    if (pageCompareCount) {
      pageCompareCount.textContent = `${count} product${count !== 1 ? 's' : ''}`;
    }
  }

  // Initialize compare system
  function initCompareSystem() {
    // Update counters on page load
    updateCompareCounters();

    // Listen for compare button clicks
    document.addEventListener('click', async function(e) {
      const compareButton = e.target.closest('.compare-button-simple');
      if (!compareButton) return;

      e.preventDefault();
      e.stopPropagation();

      const productId = compareButton.dataset.productId;
      const productTitle = compareButton.dataset.productTitle;
      const productHandle = compareButton.dataset.productHandle;
      const isInComparison = compareButton.hasAttribute('data-in-comparison');

      console.log('Compare button clicked:', { productId, productTitle, productHandle, isInComparison });

      let success = false;
      
      if (isInComparison) {
        // Remove from compare
        success = removeFromCompare(productId);
        console.log('Product removed from comparison:', success);
      } else {
        // Add to compare
        success = await addProductToCompare(productId, productTitle, productHandle);
        console.log('Product added to comparison:', success);
      }
      
      if (success) {
        // Update counters
        updateCompareCounters();
        
        // Update button state immediately after localStorage change
        setTimeout(function() {
          if (window.updateCompareButtonState) {
            window.updateCompareButtonState(productId, !isInComparison);
          }
        }, 1600); // After the button animation completes
        
        console.log('Comparison operation completed successfully');
      }
    });

    // Listen for compare events
    document.addEventListener('compare:updated', function(e) {
      updateCompareCounters(e.detail.count);
    });

    console.log('Compare system initialized');
  }

  // Expose global compare functions
  window.RendlCompare = {
    getAll: getCompareList,
    getItems: getCompareList, // Alias for consistency
    add: addProductToCompare,
    remove: removeFromCompare,
    clear: clearCompare,
    isInCompare: isInCompare,
    updateCounters: updateCompareCounters,
    getCount: function() {
      return getCompareList().length;
    }
  };
  
  // Keep old name for backward compatibility
  window.RdnlCompare = window.RendlCompare;

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCompareSystem);
  } else {
    initCompareSystem();
  }

})(); 